import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import useUrl, { type ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';

import { useAsset1003Store } from './store';
import { transformGetResponsePayload } from './utils';

const QUERY_KEYS = {
  getAssetsInformationAssetSummary: (
    obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier,
  ) => ['getAssetsInformationForAssetSummary', obfuscatedLoanIdentifier],
};

function toNumberOrUndefined(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

export function useGetAssetsSummaryInformationQuery<
  TSelected = ReturnType<typeof transformGetResponsePayload>,
>({
  select,
}: {
  select?: (data: ReturnType<typeof transformGetResponsePayload>) => TSelected;
} = {}) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: QUERY_KEYS.getAssetsInformationAssetSummary(
      obfuscatedLoanIdentifier,
    ),
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.assetsInformationGetAssetsInformationForAssetSummary(
          obfuscatedLoanIdentifier,
        );

      return transformGetResponsePayload(data);
    },
    select,
  });
}

type DeleteAssetParameter = { asset: { assetId: number; assetLdId: number } }[];

export function useDeleteAssetInformationMutation() {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { obfuscatedLoanIdentifier } = useUrl();

  return useMutation({
    mutationFn: async (list: DeleteAssetParameter) => {
      const assetRecords = list.map((item) => ({
        assetDatabaseId: item.asset.assetLdId,
        assetRecordId: item.asset.assetId,
      }));

      await typedApiClient.v1.assetsInformationBatchDeleteAssets({
        obfuscatedLoanIdentifier,
        assetRecords,
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.getAssetsInformationAssetSummary(
          obfuscatedLoanIdentifier,
        ),
      });

      dispatch(setLoanSummaryUpsertOrDelete(true));

      showSnackbar({
        severity: 'success',
        message:
          list.length > 1 ? 'Assets Deleted Successfully' : 'Asset Deleted',
        duration: 5000,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Could not delete asset',
        duration: 5000,
      });
    },
  });
}

type UpdateAdditionalReservesPayload = Parameters<
  typeof typedApiClient.v1.assetsInformationUpdateAdditionalReserves
>[0];

export function useUpdateAdditionalReservesMutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      additionalReserves,
      productType,
      reservesComment,
    }: UpdateAdditionalReservesPayload) => {
      return typedApiClient.v1.assetsInformationUpdateAdditionalReserves({
        obfuscatedLoanIdentifier,
        additionalReserves: toNumberOrUndefined(additionalReserves),
        productType,
        reservesComment,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Additional Reserves was not able to be updated.',
        duration: 5000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.getAssetsInformationAssetSummary(
          obfuscatedLoanIdentifier,
        ),
      });
      useAsset1003Store.setState({ activeForm: null });
      showSnackbar({
        severity: 'success',
        message: 'Additional Reserves Updated Successfully.',
        duration: 5000,
      });
    },
  });
}

type SaveSubordinateFinancingPayload = Parameters<
  typeof typedApiClient.v1.subordinateFinancing
>[0];

export function useSaveSubordinateFinancingMutation() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deleteSubordinateFinancing,
      creditorName,
      subordinateFinancingAmount,
      noteRate,
      paymentsPerYear,
      numberOfPayments,
      amortizingTerm,
      paymentAmount,
      helocFlag,
      highestBalance,
      interestOnly,
      undrawnAmount,
      isCommunitySecond,
      selectedCommunitySecondOption,
    }: SaveSubordinateFinancingPayload) => {
      return typedApiClient.v1.subordinateFinancing({
        obfuscatedLoanIdentifier,
        deleteSubordinateFinancing,
        creditorName,
        subordinateFinancingAmount: toNumberOrUndefined(
          subordinateFinancingAmount,
        ),
        noteRate,
        paymentsPerYear,
        numberOfPayments,
        amortizingTerm,
        paymentAmount: toNumberOrUndefined(paymentAmount),
        helocFlag,
        highestBalance,
        interestOnly,
        undrawnAmount,
        isCommunitySecond,
        selectedCommunitySecondOption,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'New Subordinate Financing was not able to be saved.',
        duration: 5000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.getAssetsInformationAssetSummary(
          obfuscatedLoanIdentifier,
        ),
      });
      useAsset1003Store.setState({ activeForm: null });
      showSnackbar({
        severity: 'success',
        message: 'New Subordinate Financing Saved Successfully.',
        duration: 5000,
      });
    },
  });
}

type SaveAssetSummaryPayload = Parameters<
  typeof typedApiClient.v1.saveAssetsSaveAssetSummary
>[0];

export function useSaveAssetSummaryMutation() {
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { obfuscatedLoanIdentifier } = useUrl();

  return useMutation({
    mutationFn: async (
      payload: Omit<SaveAssetSummaryPayload, 'obfuscatedLoanIdentifier'>,
    ) => {
      return typedApiClient.v1.saveAssetsSaveAssetSummary({
        ...payload,
        obfuscatedLoanIdentifier,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Failed to save asset.',
        duration: 5000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.getAssetsInformationAssetSummary(
          obfuscatedLoanIdentifier,
        ),
      });
      showSnackbar({
        severity: 'success',
        message: 'Asset saved successfully.',
        duration: 5000,
      });
      useAsset1003Store.setState({ activeForm: null });
      dispatch(setLoanSummaryUpsertOrDelete(true));
    },
  });
}

export function useSubordinateFinancingQuery() {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: ['subordinateFinancing', obfuscatedLoanIdentifier],
    queryFn: async () => {
      const response = await typedApiClient.v1.subordinateFinancingDetail(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
      );
      return response.data;
    },
  });
}
